import { useState, useCallback } from 'react'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  generateSls,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  saveSlsAndUpdateState,
  useSlsDispatchContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { headers, R2AppName } from '../../../../../../shared/services/config'
import { errorCollector } from '../../../../../../shared/utils/utils'
import { generateSaveDispatches } from '../utils/EditHouseholdSaveUtils'

/**
 * Encapsulates the drag-and-drop + save behavior for the Edit Households panel.
 *
 * Manages the current `dragInfo` state and exposes the DnD event handlers plus the
 * underlying save routine. A person dropped on a household (or the "new household"
 * target) is persisted via SLS and dispatched into the reducer.
 *
 * @param {Object} params
 * @param {Array} params.households - The current households (allRecords from useRecords), used to resolve the dragged person's source record.
 * @returns {{
 *   dragInfo: Object|null,
 *   dragError: Object|null,
 *   movedPersonId: string|null,
 *   clearMovedPersonId: Function,
 *   handleDragStart: Function,
 *   handleDragEnd: Function,
 *   handleDragCancel: Function,
 *   savePersonToRecord: Function,
 * }} The drag state and handlers to wire into DndContext.
 */
export const useEditHouseholdDrag = ({ households }) => {
  const zionSlsState = useSlsStateContext()
  const dispatch = useSlsDispatchContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])
  const [dragInfo, setDragInfo] = useState(null)
  const [dragError, setDragError] = useState(null)
  // The id of the person who was just successfully moved to a different household. A successful
  // move re-parents the person's draggable element to a new DroppableHousehold, so React unmounts
  // the old DOM node instead of reusing it, silently dropping keyboard focus to the document body.
  // The panel uses this to refocus the person once they re-render in their new household, so
  // screen reader users don't lose their place right as the drop announcement fires.
  const [movedPersonId, setMovedPersonId] = useState(null)
  const clearMovedPersonId = useCallback(() => setMovedPersonId(null), [])

  /**
   * Persists a person into the destination record, gated by the saveChanges feature flag.
   *
   * When the flag is on, changes are saved to SLS and the reducer is updated via
   * `saveSlsAndUpdateState`. When the flag is off, no save is performed but local state is
   * still updated so the UI reflects the move.
   *
   * On failure, `dragError` is set to `{ person }` so the panel can announce the failure to
   * screen reader users, since the DndContext's own `onDragEnd` announcement fires synchronously
   * (before this async save resolves) and optimistically reports the drop as successful.
   *
   * @param {string} recordIdParam - The destination record id, or 'new_record' to create a new household.
   * @param {Object} person - The person element being moved.
   * @returns {Promise<void>}
   */
  const savePersonToRecord = useCallback(
    async (recordIdParam, person) => {
      try {
        const { currentImageArk, images } = zionSlsState

        const dispatches = await generateSaveDispatches({
          recordIdParam,
          personsData: person,
          currentPartitionKey: currentImageArk,
          images,
          recordType: '',
        })

        const sls = generateSls(dispatches)

        await saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          slsActionsSplunkDebugger,
          dispatch,
          appName: R2AppName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            setDragError(null)
            setMovedPersonId(person?.id)
            // Processing SLS dispatches to update local state even if the saveChangesFlag is off
            if (!saveChangesFlag.isOn) {
              dispatch({
                type: 'BATCH_DISPATCHES',
                value: dispatches,
              })
            }
          },
          errorHandler: (error) => {
            errorCollector(error, zionSlsState?.currentImageArk, {
              codeId: 30,
            })
            setDragError({ person })
            setMovedPersonId(null)
          },
          saveInitiatorFilename: 'useEditHouseholdDrag.js',
        })
      } catch (error) {
        errorCollector(error, zionSlsState?.currentImageArk, {
          codeId: 30,
        })
        setDragError({ person })
        setMovedPersonId(null)
      }
    },
    [zionSlsState, dispatch, saveChangesFlag, slsActionsSplunkDebugger]
  )

  /**
   * Records the dragged person (and its source record) so the UI can reflect the in-progress drag.
   * @param {Object} event - The dnd-kit drag start event.
   */
  const handleDragStart = useCallback(
    (event) => {
      const { active } = event
      const draggedPerson = getNestedOrFloatingElementById(zionSlsState.images, active?.id)
      const record = getNestedSubelementsById(households, draggedPerson?.recordId)?.[0]
      setDragInfo({ isDragging: true, person: draggedPerson, record })
      setDragError(null)
      setMovedPersonId(null)
    },
    [zionSlsState.images, households]
  )

  /**
   * On a valid drop (a different, existing destination), saves the person; otherwise no-ops.
   * @param {Object} event - The dnd-kit drag end event.
   */
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      const sourceRecordId = active?.data?.current?.record?.id
      const destinationRecordId = over?.id

      if (sourceRecordId === destinationRecordId || !destinationRecordId) {
        setDragInfo(null)
        return
      }

      const person = getNestedOrFloatingElementById(zionSlsState.images, active?.id)
      savePersonToRecord(destinationRecordId, person)
      setDragInfo(null)
    },
    [zionSlsState.images, savePersonToRecord]
  )

  /**
   * Clears the drag state when a drag is cancelled.
   */
  const handleDragCancel = useCallback(() => {
    setDragInfo(null)
  }, [])

  return {
    dragInfo,
    dragError,
    movedPersonId,
    clearMovedPersonId,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    savePersonToRecord,
  }
}
